import React from 'react';
import PropTypes from 'prop-types';
import Toaster from './TrackerToaster';

/**
 * ActivityTracker component that tracks user activity and alerts/warns before logging out.
 *
 * @component
 * @param {object} props - Component props.
 * @param {function} [props.onLogout] - Callback function to be executed on logout.
 * @param {React.ReactNode} props.children - Child components.
 */
class ActivityTracker extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			timeForAlert: false,
		};
		this.onLogout = props.onLogout;
		// events to listen to
		this.events = [
			'load', 'click', 'scroll',
			'keypress', 'touch', 'submit'
		];
	}

	/**
	 * Lifecycle method invoked after component is mounted.
	 * Sets up event listeners for user activity.
	 */
	componentDidMount() {
		// loop for each keyboard and mouse events
		this.events.forEach((event) => {
			window.addEventListener(event, this._resetTimeout);
		});
	}

	/**
	 * Lifecycle method invoked after component updates.
	 * Reinforces setting up of event listeners.
	 */
	componentDidUpdate() {
		this.events.forEach((event) => {
			window.addEventListener(event, this._resetTimeout);
		});
	}

	/**
	 * Clears any existing warning or logout timeouts.
	 *
	 * @private
	 */
	_clearTimeout = () => {
		// when lib is made, we can do many warn times
		if (this.warnTimeout)
			clearTimeout(this.warnTimeout);

		if (this.logoutTimeout)
			clearTimeout(this.logoutTimeout);
	}

	/**
	 * Sets timeout for warning (15 minutes) and logout (30 minutes).
	 *
	 * @private
	 */
	_setTimeout = () => {
		// warn time is 15 minutes and logout time is 30 minutes
		this.warnTimeout = setTimeout(this._warn, 15 * 60 * 1000);
		this.logoutTimeout = setTimeout(this._logout, 30 * 60 * 1000);
	}

	/**
	 * Resets the warning and logout timeouts and hides alert.
	 *
	 * @private
	 */
	_resetTimeout = () => {
		this._clearTimeout();
		this._setTimeout();

		this.setState({
			timeForAlert: false
		});
	}

	/**
	 * Warns the user by setting timeForAlert to true.
	 *
	 * @private
	 */
	_warn = () => {
		this.setState({
			timeForAlert: true
		});
	}

	/**
	 * Logs out the user by clearing timeouts, resetting state,
	 * removing event listeners, and invoking onLogout callback if provided.
	 *
	 * @private
	 */
	_logout = () => {
		this._clearTimeout();
		// set state to initial state
		this.setState({
			timeForAlert: false
		});
		// remove all event listeners before logging out
		this.events.forEach((event) => {
			window.removeEventListener(event, this._resetTimeout);
		});
		if (this.onLogout)
			this.onLogout();
	}

	/**
	 * Renders the ActivityTracker component.
	 *
	 * @returns {React.ReactNode} The ActivityTracker component elements.
	 */
	render() {
		const { timeForAlert } = this.state;

		return (
			<div>
				{
					timeForAlert
						?
						<Toaster
							props={{
								header: '15 Min to Log Out',
								body: 'You will be logged out after 15 minutes'
							}}
						/>
						:
						this.props.children
				}
			</div>
		);
	}
}

ActivityTracker.propTypes = {
	onLogout: PropTypes.func,
	children: PropTypes.node,
};

export default ActivityTracker;
